<!-- AuthorForm.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useAuthorStore } from "@/stores/useAuthorStore";
import { useUserStore } from "@/stores/useUserStore";

import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Componentes modais
import AddAuthorForm from "./AddAuthorForm.vue";
import EditAuthorForm from "./EditAuthorForm.vue";
import AuthorCardModal from "./AuthorCardModal.vue";
import DeleteAuthorForm from "./DeleteAuthorForm.vue";
import BatchDeleteAuthorForm from "./BatchDeleteAuthorForm.vue";
import SearchAuthorInput from "./SearchAuthorInput.vue";
import AuthorHelpModal from "./AuthorHelpModal.vue";

// Componentes de paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

// Store
const authorStore = useAuthorStore();
const userStore = useUserStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const showCardModal = ref(false);
const selectedAuthorId = ref(null);
const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ EVITA "FLASH" DE LISTA COMPLETA (renderiza somente a página após fetch paginado)
const authorsToRender = ref([]);
const isTableReady = ref(false);
const isLoadingTable = ref(false);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ✅ TOAST UNIFICADO
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout(duration) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, duration);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(3000);
}

function handleChildNotify(payload) {
  if (!payload) return;

  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
    return;
  }

  showErrorToast(payload.message || "Ocorreu um erro.");
}

async function handleAuthorAdded() {
  if (currentPage.value !== 1) currentPage.value = 1;
  await fetchAuthorsPaged();
}

async function handleAuthorChanged() {
  await fetchAuthorsPaged();
}

async function handleAuthorDeleted() {
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  authorStore.selectedAuthorIds = [];
  await fetchAuthorsPaged();
}

// ====================================================================
// ✅ REGRA SYS / VISUAL
// ====================================================================
function normalizeUserName(name) {
  return (name ?? "").toString().trim().toLowerCase();
}

function isLoggedSys() {
  return normalizeUserName(userStore.currentUser?.userName) === "sys";
}

function isCreatedBySys(author) {
  return normalizeUserName(author?.createdByUserName) === "sys";
}

function isRowSelectable(author) {
  const logged = normalizeUserName(userStore.currentUser?.userName);
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = normalizeUserName(author?.createdByUserName);
  if (!creator) return true;

  return creator !== "sys";
}

function sysProtectedTooltip(author) {
  if (!author) return "";
  if (isLoggedSys()) return "";
  if (!isCreatedBySys(author)) return "";
  return "Protegido (criado pelo Sys)";
}

function canManage(author) {
  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = (author?.createdByUserName ?? "").trim().toLowerCase();
  if (!creator) return true;

  return creator !== "sys";
}

const anyEditableOnPage = computed(() =>
  authorsToRender.value.some((a) => isRowSelectable(a))
);

const canBatchActions = computed(() => {
  if (
    !authorStore.selectedAuthorIds ||
    authorStore.selectedAuthorIds.length === 0
  ) {
    return false;
  }

  return true;
});

const isAllSelectedOnPage = computed({
  get: () => {
    const selectableOnPage = authorsToRender.value.filter((a) =>
      isRowSelectable(a)
    );
    if (selectableOnPage.length === 0) return false;

    return selectableOnPage.every((a) =>
      authorStore.selectedAuthorIds.includes(a.authorId)
    );
  },
  set: (value) => {
    const selectableIdsOnPage = authorsToRender.value
      .filter((a) => isRowSelectable(a))
      .map((a) => a.authorId);

    const allIdsOnPage = authorsToRender.value.map((a) => a.authorId);

    if (value) {
      selectableIdsOnPage.forEach((id) => {
        if (!authorStore.selectedAuthorIds.includes(id)) {
          authorStore.selectedAuthorIds.push(id);
        }
      });
    } else {
      authorStore.selectedAuthorIds = authorStore.selectedAuthorIds.filter(
        (id) => !allIdsOnPage.includes(id)
      );
    }
  },
});

function toggleRowDropdown(authorId) {
  openDropdownId.value = openDropdownId.value === authorId ? null : authorId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const author = authorsToRender.value[index];
  if (!author) return;
  selectedAuthorId.value = author.authorId;
  authorStore.setIdAuthor(author.authorId);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(author) {
  if (!isRowSelectable(author)) return;
  authorStore.toggleSelection(author.authorId);
}

function handleBatchDelete() {
  const selectedIds = authorStore.selectedAuthorIds || [];

  if (selectedIds.length === 0) {
    showErrorToast("Nenhum autor selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }

  isBatchDropdownOpen.value = false;
  showBatchDeleteModal.value = true;
}

async function fetchAuthorsPaged() {
  try {
    isLoadingTable.value = true;

    if (!isTableReady.value) authorsToRender.value = [];

    await authorStore.fetchAuthorsPaged(
      currentPage.value,
      pageSize.value,
      authorStore.searchTerm
    );

    authorsToRender.value = Array.isArray(authorStore.authors)
      ? [...authorStore.authors]
      : [];

    isTableReady.value = true;

    if (!isLoggedSys() && authorsToRender.value.length) {
      const sysIdsOnPage = authorsToRender.value
        .filter((a) => isCreatedBySys(a))
        .map((a) => a.authorId);

      if (sysIdsOnPage.length) {
        authorStore.selectedAuthorIds = authorStore.selectedAuthorIds.filter(
          (id) => !sysIdsOnPage.includes(id)
        );
      }
    }

    if (authorStore.shouldForcePageOne) {
      authorStore.shouldForcePageOne = false;
      if (currentPage.value !== 1) {
        currentPage.value = 1;
      } else {
        await authorStore.fetchAuthorsPaged(
          1,
          pageSize.value,
          authorStore.searchTerm
        );
        authorsToRender.value = Array.isArray(authorStore.authors)
          ? [...authorStore.authors]
          : [];

        if (!isLoggedSys() && authorsToRender.value.length) {
          const sysIdsOnPage = authorsToRender.value
            .filter((a) => isCreatedBySys(a))
            .map((a) => a.authorId);

          if (sysIdsOnPage.length) {
            authorStore.selectedAuthorIds =
              authorStore.selectedAuthorIds.filter(
                (id) => !sysIdsOnPage.includes(id)
              );
          }
        }
      }
    }
  } catch (error) {
    console.error("❌ Erro ao buscar autores:", error?.message || error);
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchAuthorsPaged);

watch(
  () => authorStore.searchTerm,
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchAuthorsPaged();
    }
  }
);

watch(
  () => authorStore.authors,
  (val) => {
    if (!isTableReady.value) return;
    authorsToRender.value = Array.isArray(val) ? [...val] : [];

    if (!isLoggedSys() && authorsToRender.value.length) {
      const sysIdsOnPage = authorsToRender.value
        .filter((a) => isCreatedBySys(a))
        .map((a) => a.authorId);

      if (sysIdsOnPage.length) {
        authorStore.selectedAuthorIds = authorStore.selectedAuthorIds.filter(
          (id) => !sysIdsOnPage.includes(id)
        );
      }
    }
  }
);

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchAuthorsPaged();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  authorStore.searchTerm = "";
  authorStore.currentPage = 1;
  authorStore.shouldForcePageOne = true;

  authorStore.selectedAuthorIds = [];
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedAuthorId.value = null;

  authorsToRender.value = [];
  isTableReady.value = false;
  isLoadingTable.value = false;
});

function indexSelector(index) {
  const author = authorsToRender.value[index];
  if (!author) return;
  authorStore.setIdAuthor(author.authorId);
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Autores</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie os autores dos textos utilizados no sistema.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchAuthorInput />
        </div>
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a 1 1 0 110 2h-5v5a 1 1 0 11-2 0v-5H4a 1 1 0 110-2h5V4a 1 1 0 011-1z"
              />
            </svg>
            Adicionar Autor
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a 1 1 0 111.414 1.414l-4 4a 1 1 0 01-1.414 0l-4-4a 1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="handleBatchDelete"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>

              <th style="width: 5%">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 20%">Autor</th>
              <th style="width: 30%">Descrição</th>
              <th style="width: 15%">Criador</th>
              <th style="width: 15%">Data Atualização</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(author, index) in !isLoadingTable ? authorsToRender : []"
              :key="author.authorId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td
                @click.stop
                :class="{ 'checkbox-cell-disabled': !isRowSelectable(author) }"
              >
                <input
                  type="checkbox"
                  :value="author.authorId"
                  :checked="
                    authorStore.selectedAuthorIds.includes(author.authorId)
                  "
                  :disabled="!isRowSelectable(author)"
                  :title="sysProtectedTooltip(author)"
                  :class="[
 'checkbox-style',
                    { 'checkbox-disabled': !isRowSelectable(author) },
                  ]"
                  @change="toggleSelection(author)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <button
                  @click="toggleRowDropdown(author.authorId)"
                  class="btn-icon btn-soft-brand"
                  title="Menu de Ações"
                >
                  <i class="fas fa-ellipsis-h fa-lg"></i>
                </button>

                <div
                  v-if="openDropdownId === author.authorId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canManage(author) }"
                      @click="canManage(author) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(index)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canManage(author) }"
                      @click="canManage(author) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="author.authorId">{{ author.authorId }}</span>
              </td>

              <td class="cell-text-left">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="truncate"
                    :title="toTitleCase(author.authorName)"
                  >
                    {{ toTitleCase(author.authorName) }}
                  </span>
                </div>
              </td>

              <td class="cell-text-left">
                <span :title="author.description">
                  {{ author.description }}
                </span>
              </td>

              <td class="cell-creator">
                <div
                  class="creator-cell"
                  :title="toTitleCase(author.createdByUserName)"
                >
                  <template v-if="isCreatedBySys(author)">
                    <span class="sys-badge" title="Criado pelo Sys">SYS</span>
                  </template>
                  <template v-else>
                    <span class="truncate">
                      {{ toTitleCase(author.createdByUserName) }}
                    </span>
                  </template>
                </div>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(author.updatedAt)">
                  {{ formatDateTimeForDisplay(author.updatedAt) }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="7" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando autores...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="authorsToRender.length === 0"
              class="empty-row-state"
            >
              <td colspan="7" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum autor encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (authorStore.totalCount ?? 0) > 0"
          class="author-pagination-row"
        >
          <div class="author-pagination-left">
            <span class="author-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="authorStore.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            v-if="!isLoadingTable && (authorStore.totalCount ?? 0) > 0"
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="authorStore.totalCount ?? 0"
            :total-pages="authorStore.totalPages ?? 0"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddAuthorForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @author-added="handleAuthorAdded"
    />
    <EditAuthorForm
      v-if="showEditModal"
      @close="showEditModal = false"
      @refresh="fetchAuthorsPaged"
    />
    <AuthorCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :author-id="selectedAuthorId"
      @close="showCardModal = false"
      @author-updated="handleAuthorChanged"
      @notify="handleChildNotify"
    />
    <DeleteAuthorForm
      v-if="showDeleteModal"
      @close="showDeleteModal = false"
      @refresh="handleAuthorDeleted"
      @notify="handleChildNotify"
    />
    <BatchDeleteAuthorForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="handleAuthorDeleted"
      @notify="handleChildNotify"
    />
    <AuthorHelpModal
      v-if="showHelpModal"
      :show="showHelpModal"
      @close="showHelpModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Atenção" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.btn-pastel-green {
  background-color: #9bf6ff;
  color: #1a4a4f;
}

.row-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}

.checkbox-cell-disabled * {
  cursor: not-allowed !important;
}

.cell-creator {
  text-align: center;
}
.cell-creator > .creator-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: none;
  margin: 0;
}
.cell-creator > .creator-cell .truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.author-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}
.author-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.author-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

</style>